import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { ShowWordsSwitch } from '../components/ShowWordsSwitch';
import { storeConfig } from '../config';
import { RUNNER_AGREEMENT_VERSION } from '@aldilivery/core';

import {
  acceptJob,
  agreeToRunnerAgreement,
  ApiUnavailableError,
  fetchRunnerDashboard,
  fetchBookedTomorrow,
  type BookedTomorrow,
  sendRunnerFeedback,
  setTravelMode,
  type RunnerDashboard,
  type VehicleType,
  askAboutItem,
  declineJob,
  fetchCurrentJob,
  fetchJobQuestions,
  writeDownHeardAnswer,
  fetchOfferedJobs,
  fetchMyPay,
  fetchQueuePosition,
  fetchRunnerMe,
  moveJobOn,
  sendReceiptPhoto,
  setRunnerAvailability,
  startPaySetup,
  submitTillTotal,
  type CurrentJob,
  type ItemAnswer,
  type ItemQuestion,
  type OfferedJob,
  type QueuePosition,
  type RunnerAccount,
  type RunnerPay,
} from '../lib/api';
import { SignOutGuard } from '../lib/session-guard';
import { useOzi } from '../state/ozi';
import { InboxLink } from '../components/Inbox';
import { AgreeByVoice } from '../components/AgreeByVoice';
import { CallControls } from '../components/CallControls';
import { DocumentsChecklist } from '../components/DocumentsChecklist';
import { JobNavigation } from '../components/JobNavigation';
import { ReferralCard } from '../components/ReferralCard';
import { Reminders } from '../components/Reminders';
import { LiveClock } from '../components/LiveClock';
import { WrongAppWarning } from '../components/GetTheApp';
import {
  DOCUMENT_CHECK_WORDS,
  ENHANCED_DBS_OPTIONAL,
  PAID_BACK_PROMISE,
} from '../lib/runner-words';
import { LeaveRunning } from '../components/LeaveRunning';
import { PayoutChoice } from '../components/PayoutChoice';
import { PhotoChoice } from '../components/PhotoChoice';
import { RunnerSos } from '../components/RunnerSos';
import { HomePostcode, RunnerLocation } from '../components/RunnerLocation';
import { DemoBanner } from '../components/DemoBanner';
import { NotifyMe } from '../components/NotifyMe';
import { TillPayment } from '../components/TillPayment';
import { buzz, chime } from '../lib/alert';
import { money } from '../lib/money';
import { preparePhoto, type ReadyPhoto } from '../lib/photo';
import { clearRunnerToken, readRunnerToken } from '../lib/session';

/**
 * A Runner's page: their checks, going on shift, a job being offered, and the job in hand.
 *
 * One thing on the screen at a time, in the order a Runner meets them. While they are on shift
 * the page asks the server every few seconds whether a job has been offered, because nobody
 * should have to keep pressing refresh to earn. A new offer arrives as an alert, so a screen
 * reader says it the moment it appears; the seconds left are shown but not read out over and
 * over.
 *
 * The job steps are the lifecycle the server enforces, one button each: start shopping, put
 * in what the till said, set off, and delivered. The till total has to go in before setting
 * off, because that is what the Shopper is charged.
 *
 * While shopping, each thing on the list has a "Cannot find it" button. It asks the Shopper on
 * their own screen, and their answer comes back here and is said out loud. Nobody's phone
 * number is given to anybody (chosen by Anthony, 27 Sep 2026).
 */

const POLL_MS = 5000;

type TabKey = 'today' | 'jobs' | 'money' | 'training' | 'more';
const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'money', label: 'Money' },
  { key: 'training', label: 'Training' },
  { key: 'more', label: 'More' },
];

const TRAVEL_WORDS: Record<VehicleType, string> = {
  on_foot: 'Walking',
  bicycle: 'Bicycle',
  motorbike: 'Motorbike or moped',
  car: 'Car',
  van: 'Van',
};

/**
 * What a Runner does when something is not on the shelf (docs/BUILD_PROMPT.md, Section H): the
 * Shopper decides. Never a silent substitution, and never a Runner guessing.
 */
const NOT_ON_THE_SHELF =
  'If something is not on the shelf, ask the Shopper with the Cannot find it button: it puts the question on their screen, sends them a text and rings them, free, up to three times. They can answer on the call, on their screen or by text; the first answer counts. If they do not answer in time, leave it out. Never swap anything they have not agreed to: a product they named by brand or type is never swapped without their yes. Something open-ended, such as a crate of eggs with no brand, is yours to choose, any reasonable one. When in doubt, ask.';

const ANSWER_WORDS: Record<ItemAnswer, string> = {
  similar: 'bring something similar',
  leave_out: 'leave it out',
};

function minutesLeft(seconds: number): string {
  if (seconds < 60) return 'less than a minute';
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? 'about a minute' : `about ${minutes} minutes`;
}

/** "12.34", "£12.34" or "12" in, pence out. Null for anything that is not an amount. */
export function penceFrom(typed: string): number | null {
  const cleaned = typed.replace(/[£,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

export function RunnerHome(): JSX.Element {
  // Held as state, so forgetting the token (ruling 88) shows the signed-out page at once.
  const [signedIn, setSignedIn] = useState(() => readRunnerToken() !== null);
  const [runner, setRunner] = useState<RunnerAccount | null>(null);
  const [offers, setOffers] = useState<OfferedJob[]>([]);
  const [job, setJob] = useState<CurrentJob | null>(null);
  const [questions, setQuestions] = useState<ItemQuestion[]>([]);
  const heardAnswers = useRef<Set<string> | null>(null);
  // The call's state for each question, as last said aloud (ruling 90): each change said once.
  const heardCallStates = useRef(new Map<string, string>());
  const [loading, setLoading] = useState(signedIn);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [news, setNews] = useState('');
  const [tab, setTab] = useState<TabKey>('today');
  const [dashboard, setDashboard] = useState<RunnerDashboard | null>(null);
  // Their place in the fair queue (ruling 64): one sentence, theirs alone, said by Ozi when it
  // changes and shown in a tidy line.
  const [queue, setQueue] = useState<QueuePosition | null>(null);
  const ozi = useOzi();
  // For the poll, which is made once: Ozi as it is now.
  const oziRef = useRef(ozi);
  oziRef.current = ozi;
  const lastQueueWords = useRef<string | null>(null);
  // Raised by "Cannot find it", which starts the call in the same tap (Section H).
  const [ringNow, setRingNow] = useState(0);
  const problemRef = useRef<HTMLParagraphElement>(null);
  // Offers already chimed for, so a new one sounds once and a look every few seconds is silent.
  const heardOffers = useRef(new Set<string>());
  // Only the server's own word that the sign-in has ended, twice in a row, signs out (ruling 88).
  const signOutGuard = useRef(new SignOutGuard());

  // Every check, the face photo included (Anthony, 30 October 2026).
  const approved = runner
    ? runner.rightToWorkVerified &&
      runner.criminalRecordCheckVerified &&
      runner.facePhotoAcceptedAt !== null
    : false;

  const refresh = useCallback(async () => {
    try {
      const me = await fetchRunnerMe();
      signOutGuard.current.reset();
      setRunner(me.runner);
      // Earnings, jobs and travel. Never allowed to stop the rest of the page working.
      fetchRunnerDashboard()
        .then(setDashboard)
        .catch(() => undefined);
      const current = await fetchCurrentJob();
      setJob(current.job);
      if (current.job && current.job.status !== 'accepted') {
        const asked = (await fetchJobQuestions(current.job.orderId)).questions;
        setQuestions(asked);
        // An answer arriving while the Runner is at the shelf is said out loud, once. Answers
        // already there when the page opened are not news.
        const shopperName = current.job.shopperName;
        const opening = heardAnswers.current === null;
        heardAnswers.current ??= new Set();
        for (const question of asked) {
          // The call ringing, ringing again, answered or given up: said once per change.
          const callKey = question.call ? `${question.call.state}:${question.call.attempt}` : '';
          if (question.call && heardCallStates.current.get(question.id) !== callKey) {
            heardCallStates.current.set(question.id, callKey);
            if (!opening && question.answer === null) {
              const words = `${question.call.words} About the ${question.itemName}.`;
              setNews(words);
              void oziRef.current.say(words);
            }
          }
          if (question.answer === null || heardAnswers.current.has(question.id)) continue;
          heardAnswers.current.add(question.id);
          if (opening) continue;
          const words =
            question.answeredBy === 'shopper'
              ? `${shopperName} says: ${ANSWER_WORDS[question.answer]}, for the ${question.itemName}.`
              : `No answer in time about the ${question.itemName}, so leave it out. ${shopperName} is not charged for it.`;
          setNews(words);
          void oziRef.current.say(words);
        }
      } else {
        setQuestions([]);
      }
      // Offers come while free, and on the way to the door, when the job next door may be
      // chained to this Runner (ruling 64). A job the office sent them (ruling 80) comes even off
      // shift: it is theirs to take or decline.
      if (!current.job || current.job.status === 'delivering') {
        const offered = (await fetchOfferedJobs()).offers.filter(
          (entry) => me.runner.available || entry.allocated === true,
        );
        setOffers(offered);
        // A new offer chimes and buzzes, once (16 October 2026), for a phone in a pocket.
        const fresh = offered.filter((entry) => !heardOffers.current.has(entry.offer.id));
        fresh.forEach((entry) => heardOffers.current.add(entry.offer.id));
        if (fresh.length > 0) {
          chime();
          buzz();
        }
        // Ozi says a job from the office out loud, once (ruling 80).
        const fromOffice = fresh.find((entry) => entry.allocated && entry.allocatedWords);
        if (fromOffice?.allocatedWords) void oziRef.current.say(fromOffice.allocatedWords);
      } else {
        setOffers([]);
      }
      // Their place in line, while on shift and waiting. Never allowed to stop the page working.
      if (me.runner.available && !current.job) {
        fetchQueuePosition()
          .then(setQueue)
          .catch(() => undefined);
      } else {
        setQueue(null);
      }
    } catch (failure) {
      if (signOutGuard.current.shouldSignOut(failure)) {
        // The server has said twice, ten seconds apart, that this sign-in itself has ended
        // (ruling 88). Forget it rather than fail on every step. Nothing else ever does this:
        // a 500, a 429, a passing 401 or no answer keeps the sign-in and says so below.
        clearRunnerToken();
        setRunner(null);
        setSignedIn(false);
      } else if (failure instanceof ApiUnavailableError) {
        // Down is not the same as refused: keep the sign-in and say so.
        setProblem(failure.message);
      } else {
        setProblem(failure instanceof Error ? failure.message : 'Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!signedIn) return undefined;
    void refresh();
    const timer = window.setInterval(() => {
      void refresh();
    }, POLL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [signedIn, refresh]);

  useEffect(() => {
    if (problem !== '') problemRef.current?.focus();
  }, [problem]);

  // Ozi says their place in line when it changes (ruling 64): once per change, never every poll.
  useEffect(() => {
    const words = queue?.position === null || queue === null ? null : queue.words;
    if (words === lastQueueWords.current) return;
    lastQueueWords.current = words;
    if (words !== null) void ozi.say(words);
  }, [queue, ozi]);

  async function act(action: () => Promise<unknown>, done: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    setProblem('');
    try {
      await action();
      if (done) setNews(done);
      await refresh();
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  // Signed out only when there is no token here. A token the server has not answered for yet, or
  // answered with a passing failure, is still a sign-in (ruling 88): the problem is said below
  // and the page keeps trying, rather than telling a Runner on shift he is not signed in.
  if (!signedIn) {
    return (
      <div className="space-y-6">
        <h1 className="text-display font-bold m-0">Your Runner page</h1>
        <p className="m-0 max-w-xl">
          You are not signed in as a Runner on this device. If you have signed up already, sign in
          with your mobile number and we text you a code.
        </p>
        <Link to="/runner/sign-in" className="control bg-highlight text-on-accent">
          Sign in as a Runner
        </Link>
        <Link to="/runner/sign-up" className="control bg-paper/10 text-paper underline">
          Sign up to run
        </Link>
      </div>
    );
  }

  if (loading || !runner) {
    return problem !== '' ? (
      <div className="space-y-4">
        <p
          ref={problemRef}
          tabIndex={-1}
          role="alert"
          className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0 max-w-xl"
        >
          {problem}
        </p>
        <p className="m-0 max-w-xl">You are still signed in. We keep trying.</p>
      </div>
    ) : (
      <p role="status" className="m-0">
        One moment.
      </p>
    );
  }

  // The first offer with a job behind it; one at a time is all a Runner is ever shown.
  const first = offers[0];
  const offer = first?.job ? { ...first, job: first.job } : null;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-display font-bold m-0">Hello, {runner.name}</h1>
        <LiveClock />
      </div>
      {runner.isDemo && <DemoBanner />}
      <nav aria-label="Your messages">
        <InboxLink as="runner" to="/runner/messages" />
      </nav>
      <details className="max-w-xl">
        <summary className="control px-0 text-paper underline">The screen</summary>
        <ShowWordsSwitch />
      </details>

      <p role="status" className="m-0 min-h-control">
        {news}
      </p>

      {problem !== '' && (
        <p
          ref={problemRef}
          tabIndex={-1}
          role="alert"
          className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0 max-w-xl"
        >
          {problem}
        </p>
      )}

      {/* Areas (Anthony, 30 October 2026): jobs come from their own area, nearest first. A Runner
          with no home postcode yet is asked here, above the tabs, on every tab, approved or not
          (30 October 2026); nothing else waits for it. */}
      {!runner.isDemo && !runner.homePostcode?.trim() && (
        <HomePostcode
          onSaved={(message) => {
            setNews(message);
            void refresh();
          }}
        />
      )}

      <nav aria-label="Your Runner pages">
        <ul className="chip-row list-none m-0 p-0">
          {TABS.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                aria-current={tab === item.key ? 'page' : undefined}
                onClick={() => setTab(item.key)}
                className={`control ${tab === item.key ? 'border-paper bg-paper text-ink' : 'bg-paper/10 text-paper'}`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {tab === 'today' && (
        <>
          {dashboard && approved && (
            <section aria-labelledby="today-heading" className="space-y-1 max-w-xl">
              <h2 id="today-heading" className="visually-hidden">
                Earned today
              </h2>
              <p className="m-0 text-display font-bold">
                {money(dashboard.earnings.todayPence)} today
              </p>
              <p className="m-0">
                {dashboard.earnings.jobsToday} {dashboard.earnings.jobsToday === 1 ? 'job' : 'jobs'}{' '}
                today. {money(dashboard.earnings.weekPence)} this week.
              </p>
            </section>
          )}
          {dashboard?.insurance && (
            <section aria-labelledby="insurance-heading" className="space-y-2 max-w-xl">
              <h2 id="insurance-heading" className="text-lead font-bold">
                Your motor insurance
              </h2>
              <p className="m-0">{dashboard.insurance.words}</p>
            </section>
          )}
          {dashboard && !job && (
            <TravelToday
              dashboard={dashboard}
              busy={busy}
              onChoose={(mode) => {
                void act(async () => {
                  const result = await setTravelMode(mode);
                  setNews(result.message);
                }, '');
              }}
            />
          )}
          {approved && <RunnerLocation onShift={runner.available} onJob={job !== null} />}
          {!approved ? (
            <section aria-labelledby="checks-heading" className="space-y-3 max-w-xl">
              <h2 id="checks-heading" className="text-lead font-bold">
                Waiting for your checks
              </h2>
              <p className="m-0">
                Send your documents below. {DOCUMENT_CHECK_WORDS} You can go on shift once all of
                these are done.
              </p>
              <ul className="m-0 ps-6 space-y-2">
                <li>
                  Right to work in the United Kingdom:{' '}
                  {runner.rightToWorkVerified ? 'done' : 'not yet'}.
                </li>
                <li>
                  Basic criminal record check (DBS):{' '}
                  {runner.criminalRecordCheckVerified ? 'done' : 'not yet'}.
                </li>
                <li>Face photo: {runner.facePhotoAcceptedAt ? 'done' : 'not yet'}.</li>
              </ul>
              <p className="m-0">{ENHANCED_DBS_OPTIONAL}</p>
              <DocumentsChecklist onNews={setNews} />
            </section>
          ) : job ? (
            <>
              <JobInHand
                job={job}
                questions={questions}
                busy={busy}
                act={act}
                onNews={setNews}
                travelling={dashboard?.travelling ?? 'on_foot'}
                onAsked={() => setRingNow((count) => count + 1)}
              />
              <CallControls
                orderId={job.orderId}
                as="runner"
                otherName={job.shopperName}
                ringNow={ringNow}
              />
              <RunnerSos />
              <Link
                to={`/runner/jobs/${encodeURIComponent(job.orderId)}/problem`}
                className="control bg-paper/10 text-paper underline"
              >
                Report a problem with this job
              </Link>
            </>
          ) : (
            <>
              {runner.agreementCurrent === false && (
                <AgreeFirst
                  changed={Boolean(runner.agreementAcceptedAt)}
                  busy={busy}
                  onAgree={(channel) => {
                    void act(async () => {
                      const result = await agreeToRunnerAgreement(
                        RUNNER_AGREEMENT_VERSION,
                        channel,
                      );
                      setNews(result.message);
                    }, '');
                  }}
                />
              )}
              <section aria-labelledby="shift-heading" className="space-y-3 max-w-xl">
                <h2 id="shift-heading" className="text-lead font-bold">
                  {runner.available ? 'You are on shift' : 'You are off shift'}
                </h2>
                <p className="m-0">
                  {runner.available
                    ? 'Jobs near you will appear here. You do not need to keep refreshing.'
                    : 'Go on shift when you are ready to take jobs.'}
                </p>
                <BookedForTomorrow />
                {runner.available && queue && queue.position !== null && (
                  <p className="m-0">
                    <strong>{placeInLine(queue.position)}</strong>
                    <span className="extra"> {queue.words}</span>
                  </p>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const next = !runner.available;
                    void act(
                      () => setRunnerAvailability(next),
                      next ? 'You are on shift.' : 'You are off shift.',
                    );
                  }}
                  className="control w-full bg-highlight text-on-accent text-lead disabled:opacity-70"
                >
                  {runner.available ? 'Go off shift' : 'Go on shift'}
                </button>
              </section>

              {(runner.available || offer?.allocated === true) && offer && (
                <JobOffer offer={offer} busy={busy} act={act} onNews={setNews} />
              )}
            </>
          )}
          {/* On the way to the door, the job next door may be offered now (ruling 64). */}
          {job &&
            job.status === 'delivering' &&
            (runner.available || offer?.allocated === true) &&
            offer && <JobOffer offer={offer} busy={busy} act={act} onNews={setNews} />}
          {approved && !job && <TillPayment onNews={setNews} />}
          {approved && <HowYouGetPaid />}
          {/* So a job the office sends reaches them with the page closed (ruling 80). */}
          {approved && <NotifyMe as="runner" />}
        </>
      )}

      {tab === 'jobs' && <JobHistory dashboard={dashboard} />}
      {tab === 'money' && (
        <>
          <Money dashboard={dashboard} />
          <div className="max-w-xl">
            <PayoutChoice onNews={setNews} />
          </div>
        </>
      )}
      {tab === 'training' && <Training />}
      {tab === 'more' && (
        <>
          <More dashboard={dashboard} onNews={setNews} />
          <Reminders as="runner" />
          {!dashboard?.leftAt && (
            <div className="max-w-xl">
              <LeaveRunning
                onNews={(news) => {
                  setNews(news);
                  void refresh();
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** "First in line", "Second in line": the short form for the screen. Position 0 is first. */
export function placeInLine(position: number): string {
  const ordinal = [
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
  ][position];
  return ordinal ? `${ordinal} in line` : `Number ${position + 1} in line`;
}

/** Where the shopping comes from: the Shop Partners by name, or "the shops on the list". */
function fromShops(names: readonly string[] | undefined): string {
  if (!names || names.length === 0) return 'the shops on the list';
  if (names.length === 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/**
 * A job being offered: a summary, the seconds left, yes or no. Arrives as an alert, so a screen
 * reader says it the moment it appears. A chained offer (ruling 64) says it is the next job, near
 * where the Runner is now, and that no costs nothing.
 */
function JobOffer({
  offer,
  busy,
  act,
  onNews,
}: {
  offer: OfferedJob & { job: NonNullable<OfferedJob['job']> };
  busy: boolean;
  act: (action: () => Promise<unknown>, done: string) => Promise<void>;
  onNews: (text: string) => void;
}): JSX.Element {
  return (
    <>
      <section
        role="alert"
        aria-labelledby="offer-heading"
        className="space-y-3 max-w-xl border-2 border-highlight rounded-2xl p-4"
      >
        <h2 id="offer-heading" className="text-lead font-bold m-0">
          {offer.allocated
            ? 'A job from the office'
            : offer.job.split
              ? offer.job.split.label
              : offer.chained
                ? 'Your next job'
                : 'A job for you'}
        </h2>
        {offer.allocated && offer.allocatedWords && <p className="m-0">{offer.allocatedWords}</p>}
        {offer.chained && offer.chainedWords && <p className="m-0">{offer.chainedWords}</p>}
        {offer.job.split && <p className="m-0 font-bold">{offer.job.split.earnWords}</p>}
        {offer.job.split && <p className="m-0">{offer.job.split.rest}</p>}
        <p className="m-0">
          {/* No supermarket partnership: the shops by name when known (Anthony, 30 October 2026). */}
          From {fromShops(offer.job.shopNames)}
          {offer.job.distanceMiles !== null
            ? `, ${offer.job.distanceMiles.toFixed(1)} miles ${offer.chained ? 'from this door' : 'away'}`
            : ''}
          . {offer.job.itemCount} {offer.job.itemCount === 1 ? 'thing' : 'things'}, about{' '}
          {money(offer.job.goodsEstimatePence)} of shopping. You get{' '}
          {money(offer.job.runnerPaymentPence)}.
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            void act(async () => {
              const taken = await acceptJob(offer.offer.id);
              // How to pay at the till for this one: the card loaded, or their own.
              onNews(taken.till ? `The job is yours. ${taken.till.message}` : 'The job is yours.');
            }, '');
          }}
          className="control w-full bg-highlight text-on-accent text-lead disabled:opacity-70"
        >
          Take this job
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            void act(
              () => declineJob(offer.offer.id),
              offer.allocated
                ? 'No problem. It goes back to the office.'
                : 'No problem. It will go to somebody else.',
            );
          }}
          className="control bg-paper/10 text-paper underline"
        >
          Not this one
        </button>
      </section>
      <p className="m-0 max-w-xl">
        {offer.secondsLeft > 120
          ? `About ${Math.round(offer.secondsLeft / 60)} minutes left to say yes.`
          : `About ${offer.secondsLeft} seconds left to say yes.`}
      </p>
    </>
  );
}

function JobInHand({
  job,
  questions,
  busy,
  act,
  onNews,
  travelling,
  onAsked,
}: {
  job: CurrentJob;
  questions: ItemQuestion[];
  busy: boolean;
  act: (action: () => Promise<unknown>, done: string) => Promise<void>;
  onNews: (text: string) => void;
  /** How they are travelling today, for the directions. */
  travelling: VehicleType;
  /** "Cannot find it" was asked: the call to the Shopper starts in the same tap. */
  onAsked: () => void;
}): JSX.Element {
  const [tillError, setTillError] = useState('');
  // The photo of the receipt (STILL_TO_DO item 2): optional, but it lets a larger pay-back go
  // without waiting for a person.
  const [photo, setPhoto] = useState<ReadyPhoto | null>(null);
  const [photoNote, setPhotoNote] = useState('');
  // Paying at the till with the spending card, loaded for this order (9 October 2026).
  const byCard = job.payMethodUsed === 'card';

  async function takePhoto(file: File | undefined): Promise<ReadyPhoto | null> {
    if (!file) return null;
    try {
      return await preparePhoto(file);
    } catch (failure) {
      setPhotoNote(failure instanceof Error ? failure.message : 'That photo could not be read.');
      return null;
    }
  }

  function onTill(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const typed = String(new FormData(event.currentTarget).get('till') ?? '');
    const pence = penceFrom(typed);
    if (pence === null) {
      setTillError('Please type the total from the receipt, like 12.34.');
      return;
    }
    setTillError('');
    // What we say back includes being paid back for the shopping (ruling 55), in plain words.
    void act(async () => {
      const result = await submitTillTotal(job.orderId, pence, photo ?? undefined);
      onNews(`Thank you. The till total of ${money(pence)} is in. ${result.message}`);
    }, '');
  }

  return (
    <section aria-labelledby="job-heading" className="space-y-6 max-w-xl">
      <h2 id="job-heading" className="text-lead font-bold">
        Your job: shopping for {job.shopperName}
      </h2>
      {job.split && (
        <div className="space-y-1 border-2 border-highlight rounded-2xl p-4">
          <p className="m-0 font-bold">{job.split.label}</p>
          <p className="m-0">{job.split.rest}</p>
        </div>
      )}

      {byCard && (job.status === 'accepted' || job.status === 'shopping') && (
        <div className="space-y-2 border-2 border-highlight rounded-2xl p-4">
          <h3 className="text-lead font-bold m-0">Paying at the till</h3>
          <p className="m-0">
            Your {storeConfig.assistantName} card is loaded with up to{' '}
            {money(job.cardLimitPence ?? 0)} for this order.
          </p>
          <p className="m-0 text-display font-bold">Tap your phone at the till</p>
          <p className="m-0">
            Hold your phone near the card reader and choose your {storeConfig.assistantName} card,
            not your own. It works in grocery shops only, for this order only. You do not need any
            of your own money.
          </p>
          <p className="m-0">
            If the card is declined, pay with your own card instead and put in the till total as
            usual. {PAID_BACK_PROMISE}
          </p>
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-lead font-bold m-0">The list</h3>
        <ul className="m-0 ps-6 space-y-3">
          {job.items.map((item) => {
            const question = questions.find((asked) => asked.orderItemId === item.id);
            return (
              <li key={item.id} className="space-y-2">
                <span>
                  {item.quantity} × {item.name}
                </span>
                {question && <p className="m-0">{questionWords(question, job.shopperName)}</p>}
                {question && question.answer === null && question.call?.state === 'live' && (
                  // They answered the call and said it: the Runner writes down which (ruling 90).
                  <div className="flex flex-wrap gap-2">
                    {(['similar', 'leave_out'] as const).map((heard) => (
                      <button
                        key={heard}
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          void act(
                            () => writeDownHeardAnswer(job.orderId, question.id, heard),
                            `Written down: ${job.shopperName} said ${ANSWER_WORDS[heard]}, for the ${item.name}.`,
                          );
                        }}
                        aria-label={`${job.shopperName} said: ${ANSWER_WORDS[heard]}, for the ${item.name}`}
                        className="control bg-paper text-ink disabled:opacity-70"
                      >
                        They said: {ANSWER_WORDS[heard]}
                      </button>
                    ))}
                  </div>
                )}
                {job.status === 'shopping' && !question && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      // One tap (ruling 90): the question on their screen, the text, and the free
                      // call the server rings; this page joins it (Section H).
                      void act(async () => {
                        const asked = await askAboutItem(job.orderId, item.id);
                        if (asked.callsOff) {
                          onNews(
                            `We have asked ${job.shopperName} about the ${item.name} on their screen and by text. Calls are off, so nothing rings.`,
                          );
                        } else {
                          onNews(
                            `We have asked ${job.shopperName} about the ${item.name}. Their answer will appear here.`,
                          );
                          onAsked();
                        }
                      }, '');
                    }}
                    aria-label={`Cannot find it: ${item.name}`}
                    className="control bg-paper/10 text-paper underline disabled:opacity-70"
                  >
                    Cannot find it
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        <p className="m-0">{NOT_ON_THE_SHELF}</p>
      </div>

      <div className="space-y-2">
        <h3 className="text-lead font-bold m-0">Where it goes</h3>
        <p className="m-0">{job.deliveryAddress}</p>
        {job.doorstepProtocol !== '' && (
          <p className="m-0">At the door, in their words: {job.doorstepProtocol}</p>
        )}
        {job.doorWord && (
          <>
            <p className="m-0 text-lead">
              At the door, say: <strong>{job.doorWord}</strong>
            </p>
            <p className="m-0 extra">
              {job.shopperName} has been given these two words, so they know it is you knocking. Say
              them before anything else. Never say any numbers instead.
            </p>
          </>
        )}
      </div>

      {job.counterWords && (
        <p className="m-0 text-lead font-bold border-2 border-highlight rounded-2xl p-4">
          {job.counterWords}
        </p>
      )}

      <JobNavigation
        address={job.deliveryAddress}
        shopNames={job.shopNames}
        mode={travelling}
        stage={job.status === 'accepted' || job.status === 'shopping' ? 'to-shop' : 'to-door'}
      />

      <div className="space-y-3">
        <h3 className="text-lead font-bold m-0">Next</h3>
        {job.status === 'accepted' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void act(() => moveJobOn(job.orderId, 'shopping'), 'You are shopping.');
            }}
            className="control w-full bg-highlight text-on-accent text-lead disabled:opacity-70"
          >
            I have started shopping
          </button>
        )}

        {job.status === 'shopping' && (
          <form onSubmit={onTill} noValidate className="space-y-3">
            <label htmlFor="till" className="block text-lead font-bold">
              What did the till say?
            </label>
            <p id="till-hint" className="m-0 text-paper/90">
              {byCard
                ? `The total on the receipt, in pounds and pence. The shop was paid with your ${storeConfig.assistantName} card, and we check the two match.`
                : `The total on the receipt, in pounds and pence. The Shopper pays the till total, within the limits in our terms. ${PAID_BACK_PROMISE}`}
            </p>
            {tillError !== '' && (
              <p
                role="alert"
                className="m-0 border-2 border-paper bg-paper text-ink p-3 rounded-2xl"
              >
                {tillError}
              </p>
            )}
            <input
              id="till"
              name="till"
              type="text"
              inputMode="decimal"
              aria-describedby="till-hint"
              className="w-full max-w-xs min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
            />
            <PhotoChoice
              takeLabel={photo ? 'Take the receipt photo again' : 'Take a photo of the receipt'}
              chooseLabel="Choose a photo of the receipt"
              describedBy="receipt-photo-hint"
              takeClassName="control bg-paper/10 text-paper underline"
              chooseClassName="control bg-paper/10 text-paper underline"
              onFile={(file) => {
                void takePhoto(file).then((ready) => {
                  if (!ready) return;
                  setPhoto(ready);
                  setPhotoNote('The photo of the receipt is ready to go with the total.');
                });
              }}
            />
            <p id="receipt-photo-hint" className="m-0">
              Optional, but it helps: without a photo, anything over{' '}
              {money(storeConfig.receipts.photoNeededAbovePence)} waits for a person to check before
              we pay you back.
            </p>
            <p role="status" className="m-0">
              {photoNote}
            </p>
            <button
              type="submit"
              disabled={busy}
              className="control w-full bg-highlight text-on-accent text-lead disabled:opacity-70"
            >
              Put in the till total
            </button>
          </form>
        )}

        {job.reimbursementStatus && job.reimbursementPence != null && (
          <p className="m-0">{payBackWords(job.reimbursementStatus, job.reimbursementPence)}</p>
        )}

        {job.reimbursementStatus === 'waiting' && job.status !== 'shopping' && (
          <>
            <PhotoChoice
              takeLabel="Add a photo of the receipt"
              chooseLabel="Choose a photo of the receipt"
              disabled={busy}
              takeClassName="control bg-paper/10 text-paper underline"
              chooseClassName="control bg-paper/10 text-paper underline"
              onFile={(file) => {
                void takePhoto(file).then((ready) => {
                  if (!ready) return;
                  void act(async () => {
                    const result = await sendReceiptPhoto(job.orderId, ready);
                    onNews(result.message);
                  }, '');
                });
              }}
            />
            <p role="status" className="m-0">
              {photoNote}
            </p>
          </>
        )}

        {job.status === 'receipt_submitted' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void act(() => moveJobOn(job.orderId, 'delivering'), 'You are on your way.');
            }}
            className="control w-full bg-highlight text-on-accent text-lead disabled:opacity-70"
          >
            I am on my way
          </button>
        )}

        {job.status === 'delivering' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void act(async () => {
                const result = (await moveJobOn(job.orderId, 'delivered')) as {
                  notes?: string[];
                };
                onNews(
                  result.notes && result.notes.length > 0
                    ? `Delivered. Thank you. ${result.notes.join(' ')}`
                    : `Delivered. Thank you. You have earned ${money(job.runnerPaymentPence)} for this one.`,
                );
              }, '');
            }}
            className="control w-full bg-highlight text-on-accent text-lead disabled:opacity-70"
          >
            I have delivered it
          </button>
        )}
      </div>
    </section>
  );
}

/** The live state of a question (ruling 90): the call, then the answer, in the Runner's words. */
function questionWords(question: ItemQuestion, shopperName: string): string {
  if (question.answer === null) {
    const waiting = `Asked ${shopperName}. Waiting for an answer, ${minutesLeft(question.secondsLeft)} left. If there is no answer: ${ANSWER_WORDS[question.ifNoAnswer]}.`;
    return question.call ? `${question.call.words} ${waiting}` : waiting;
  }
  if (question.answeredBy !== 'shopper') {
    return 'No answer: leave it out. They are not charged for it.';
  }
  const how =
    question.answeredVia === 'call'
      ? ' (on the call)'
      : question.answeredVia === 'text'
        ? ' (by text)'
        : '';
  return `${shopperName} says${how}: ${ANSWER_WORDS[question.answer]}.`;
}

/**
 * Where a Runner's pay goes. Their bank details go into Stripe's own form, never into
 * the service: this only starts that, and says plainly what has been earned and what is waiting.
 */
function HowYouGetPaid(): JSX.Element {
  const [pay, setPay] = useState<RunnerPay | null>(null);
  const [problem, setProblem] = useState('');
  const [going, setGoing] = useState(false);
  const back = new URLSearchParams(window.location.search).get('pay');

  useEffect(() => {
    fetchMyPay()
      .then(setPay)
      .catch((failure: unknown) => {
        setProblem(failure instanceof Error ? failure.message : 'We could not check your pay.');
      });
  }, []);

  async function setUp(): Promise<void> {
    setGoing(true);
    setProblem('');
    try {
      const { url } = await startPaySetup();
      window.location.assign(url);
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : 'We could not open the form.');
      setGoing(false);
    }
  }

  return (
    <section aria-labelledby="pay-heading" className="space-y-3 max-w-xl">
      <h2 id="pay-heading" className="text-lead font-bold">
        How you get paid
      </h2>
      {problem !== '' && (
        <p role="alert" className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0">
          {problem}
        </p>
      )}
      {pay === null ? (
        problem === '' && <p className="m-0">Checking.</p>
      ) : (
        <>
          <p className="m-0">
            You have earned {money(pay.totalEarnedPence)} from {pay.completedDeliveryCount}{' '}
            {pay.completedDeliveryCount === 1 ? 'delivery' : 'deliveries'}.
            {pay.owedPence > 0 &&
              ` ${money(pay.owedPence)} more is owed to you and is sent as soon as your bank details are set up.`}
          </p>
          {pay.setup === 'ready' ? (
            <p role={back ? 'status' : undefined} className="m-0">
              Your pay goes to your Stripe account, then to your bank on your payout schedule.
            </p>
          ) : (
            <>
              <p role={back ? 'status' : undefined} className="m-0">
                {pay.setup === 'not_started'
                  ? `Before we can pay you, we need to know where to send the money. You give your bank details to our payment company, Stripe, on their own page. ${storeConfig.productName} never sees them.`
                  : 'You have started setting up where your pay goes, but Stripe needs a little more from you before it can send money.'}
              </p>
              <button
                type="button"
                disabled={going}
                onClick={() => {
                  void setUp();
                }}
                className="control w-full bg-highlight text-on-accent text-lead disabled:opacity-70"
              >
                {going
                  ? 'Opening Stripe…'
                  : pay.setup === 'not_started'
                    ? 'Set up how you get paid'
                    : 'Finish setting up how you get paid'}
              </button>
            </>
          )}
        </>
      )}
    </section>
  );
}

/**
 * How they are delivering today (ruling, 2 October 2026). Walking or cycling can be chosen at
 * any time, whatever they signed up with. A car, motorbike or van needs their licence and insurance
 * checked and in date; the server says so if not.
 */
function TravelToday({
  dashboard,
  busy,
  onChoose,
}: {
  dashboard: RunnerDashboard;
  busy: boolean;
  onChoose: (mode: VehicleType) => void;
}): JSX.Element {
  const choices = [...new Set<VehicleType>(['on_foot', 'bicycle', ...dashboard.travelModes])];
  return (
    <section aria-labelledby="travel-heading" className="space-y-3 max-w-xl">
      <h2 id="travel-heading" className="text-lead font-bold">
        Today you are delivering: {TRAVEL_WORDS[dashboard.travelling].toLowerCase()}
      </h2>
      <div className="flex flex-wrap gap-2">
        {choices
          .filter((mode) => mode !== dashboard.travelling)
          .map((mode) => (
            <button
              key={mode}
              type="button"
              disabled={busy}
              onClick={() => onChoose(mode)}
              className="control border-paper bg-paper/10 text-paper disabled:opacity-70"
            >
              Switch to {TRAVEL_WORDS[mode].toLowerCase()}
            </button>
          ))}
      </div>
    </section>
  );
}

function day(at: string): string {
  return new Date(at).toLocaleString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Every job they have done: when, where (the area only), what they earned, and its reference. */
function JobHistory({ dashboard }: { dashboard: RunnerDashboard | null }): JSX.Element {
  return (
    <section aria-labelledby="jobs-heading" className="space-y-3 max-w-xl">
      <h2 id="jobs-heading" className="text-lead font-bold">
        Jobs you have done
      </h2>
      {dashboard === null ? (
        <p className="m-0">Finding your jobs.</p>
      ) : dashboard.jobs.length === 0 ? (
        <p className="m-0">No jobs yet. They will be listed here, newest first.</p>
      ) : (
        <ul className="list-none m-0 p-0 space-y-3">
          {dashboard.jobs.map((job) => (
            <li key={job.reference} className="border-2 border-paper/40 rounded-2xl p-4">
              <p className="m-0 font-bold">
                {money(job.earnedPence)}, {day(job.deliveredAt)}
              </p>
              <p className="m-0">
                Area {job.area}. Order {job.reference}. {job.paid ? 'Paid.' : 'Not paid yet.'}
              </p>
              <Link
                to={`/runner/jobs/${encodeURIComponent(job.orderId)}/problem`}
                className="control bg-paper/10 text-paper underline mt-2"
              >
                Report a problem<span className="visually-hidden"> with order {job.reference}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** What they have earned and what has been paid out, big and plain. */
/** Where paying a Runner back for the shopping has got to, in plain words (ruling 55). */
function payBackWords(status: 'paid' | 'waiting' | 'owed', pence: number): string {
  if (status === 'paid') return `You've been paid back ${money(pence)} for the shopping.`;
  if (status === 'waiting') {
    return `A person is checking the till total before we pay you back ${money(pence)} for the shopping.`;
  }
  return `We owe you ${money(pence)} for the shopping. It is sent as soon as your bank details are set up.`;
}

/**
 * Agreeing to the Runner agreement before the first job (ruling 55). A tick and a button, kept
 * with the date and the version. No job is offered until it is done.
 */
function AgreeFirst({
  busy,
  changed = false,
  onAgree,
}: {
  busy: boolean;
  /** They agreed to an earlier version: the agreement has changed since (16 October 2026). */
  changed?: boolean;
  onAgree: (channel: 'button' | 'voice') => void;
}): JSX.Element {
  const [ticked, setTicked] = useState(false);
  const [problem, setProblem] = useState('');
  return (
    <section aria-labelledby="agree-heading" className="space-y-3 max-w-xl">
      <h2 id="agree-heading" className="text-lead font-bold">
        {changed ? 'The Runner agreement has changed' : 'Before your first job'}
      </h2>
      <p className="m-0">
        {changed
          ? 'The Runner agreement has changed. Please read it and agree before your next job. No job is offered to you until you have agreed.'
          : 'Please read the Runner agreement and say you agree to it. It says how you are paid, how we pay you back for the shopping, and what happens if something goes wrong. No job is offered to you until you have agreed.'}
      </p>
      <Link to="/runner/agreement" className="control bg-paper/10 text-paper underline">
        Read the Runner agreement
      </Link>
      {problem !== '' && (
        <p role="alert" className="m-0 border-2 border-paper bg-paper text-ink p-3 rounded-2xl">
          {problem}
        </p>
      )}
      <div className="flex items-center gap-3 min-h-control">
        <input
          type="checkbox"
          id="agree-runner"
          checked={ticked}
          onChange={(event) => setTicked(event.target.checked)}
          className="h-6 w-6"
        />
        <label htmlFor="agree-runner" className="m-0">
          I have read the Runner agreement and I agree to it
        </label>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          if (!ticked) {
            setProblem('Please tick the box to say you agree, or read the agreement first.');
            return;
          }
          setProblem('');
          onAgree('button');
        }}
        className="control w-full bg-highlight text-on-accent text-lead disabled:opacity-70"
      >
        I agree
      </button>
      <AgreeByVoice busy={busy} onAgree={() => onAgree('voice')} />
    </section>
  );
}

function Money({ dashboard }: { dashboard: RunnerDashboard | null }): JSX.Element {
  if (!dashboard) {
    return (
      <p role="status" className="m-0">
        Working out your money.
      </p>
    );
  }
  return (
    <div className="space-y-6 max-w-xl">
      <section aria-labelledby="earned-heading" className="space-y-2">
        <h2 id="earned-heading" className="text-lead font-bold">
          What you have earned
        </h2>
        <p className="m-0 text-display font-bold">{money(dashboard.earnings.todayPence)} today</p>
        <p className="m-0 text-lead font-bold">{money(dashboard.earnings.weekPence)} this week</p>
        <p className="m-0 text-lead font-bold">{money(dashboard.earnings.allTimePence)} in all</p>
      </section>
      {dashboard.owing && dashboard.owing.length > 0 && (
        <section aria-labelledby="owing-heading" className="space-y-2">
          <h2 id="owing-heading" className="text-lead font-bold">
            Being repaid
          </h2>
          <p className="m-0">
            After a decision that you were responsible for a refund,{' '}
            {dashboard.recoveryPercentOfPay ?? 10}% of each job&rsquo;s pay goes towards it until it
            is repaid. You always keep the rest.
          </p>
          <ul className="m-0 ps-6 space-y-2">
            {dashboard.owing.map((item) => (
              <li key={item.reference + item.amountPence}>
                Order {item.reference}: {money(item.remainingPence)} left of{' '}
                {money(item.amountPence)}. {item.reason}
              </li>
            ))}
          </ul>
        </section>
      )}
      {dashboard.paidBack && dashboard.paidBack.length > 0 && (
        <section aria-labelledby="paid-back-heading" className="space-y-2">
          <h2 id="paid-back-heading" className="text-lead font-bold">
            Paid back for the shopping
          </h2>
          <p className="m-0">
            Your own money, paid back for what you bought at the till. It is not counted as
            earnings. {money(dashboard.paidBackTotalPence ?? 0)} paid back so far.
          </p>
          <ul className="m-0 ps-6 space-y-2">
            {dashboard.paidBack.map((item) => (
              <li key={item.reference + item.at}>
                Order {item.reference}: {payBackWords(item.status, item.pence)}
              </li>
            ))}
          </ul>
        </section>
      )}
      <section aria-labelledby="paid-heading" className="space-y-2">
        <h2 id="paid-heading" className="text-lead font-bold">
          Paid out to you
        </h2>
        <p className="m-0">
          {money(dashboard.totalTransferredPence)} sent to your bank account so far.
        </p>
        {dashboard.payouts.length > 0 && (
          <ul className="m-0 ps-6 space-y-2">
            {dashboard.payouts.map((payout) => (
              <li key={payout.reference + payout.at}>
                {money(payout.transferredPence)} for order {payout.reference}, {day(payout.at)}
                {payout.coolBagWithheldPence > 0
                  ? `, after ${money(payout.coolBagWithheldPence)} towards your cool box, which you get back`
                  : ''}
                .
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * Training (T11). The modules are being written; this is where they will be, each spoken and
 * written, with a record when passed. They are optional: no job waits for them (Anthony, 30
 * October 2026).
 */
const MODULES = [
  'Guiding and handing over to a blind or partially sighted person',
  'The door safe word',
  'The handover photograph, and asking first',
  'Calling the Shopper when something is not on the shelf',
  'Food hygiene and the cool box',
  'Working alone safely, and the SOS button',
  'What can and cannot be sent',
  'Safeguarding and the wellbeing check',
  "Looking after people's information",
  'Insurance for delivery work',
];

function Training(): JSX.Element {
  return (
    <section aria-labelledby="training-heading" className="space-y-3 max-w-xl">
      <h2 id="training-heading" className="text-lead font-bold">
        Training
      </h2>
      <p className="m-0">
        Short optional lessons, read aloud and written, to help you do this job well and grow. They
        are being written now, and no job depends on them. Each one will show here with a mark when
        you have finished it.
      </p>
      <ol className="m-0 ps-6 space-y-2">
        {MODULES.map((module) => (
          <li key={module}>{module}: coming soon.</li>
        ))}
      </ol>
    </section>
  );
}

/** Their ID and link, their documents, and a way to tell us things. */
function More({
  dashboard,
  onNews,
}: {
  dashboard: RunnerDashboard | null;
  onNews: (news: string) => void;
}): JSX.Element {
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [sending, setSending] = useState(false);
  const [feedbackProblem, setFeedbackProblem] = useState('');

  return (
    <div className="space-y-8 max-w-xl">
      {/* Another app called Ozi is in the app stores; ours is not yet (Anthony, 30 October 2026). */}
      <WrongAppWarning homeScreen />
      {dashboard && (
        <section aria-labelledby="id-heading" className="space-y-3">
          <h2 id="id-heading" className="text-lead font-bold">
            Your Runner ID
          </h2>
          <p className="m-0">
            Your Runner ID is{' '}
            <span className="font-bold">{dashboard.runnerId.split('').join(' ')}</span>.
          </p>
        </section>
      )}

      {/* Their referral link, which brings anybody, and Ozi Ambassadors (30 October 2026). */}
      <ReferralCard as="runner" onNews={onNews} />

      <section aria-labelledby="documents-heading" className="space-y-3">
        <h2 id="documents-heading" className="text-lead font-bold">
          Your documents
        </h2>
        <DocumentsChecklist onNews={onNews} />
      </section>

      <section aria-labelledby="feedback-heading" className="space-y-3">
        <h2 id="feedback-heading" className="text-lead font-bold">
          Tell us something
        </h2>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (message.trim() === '') {
              setFeedbackProblem('Please say what you would like to tell us.');
              return;
            }
            setSending(true);
            setFeedbackProblem('');
            sendRunnerFeedback(message.trim(), anonymous)
              .then((result) => {
                setMessage('');
                onNews(result.message);
              })
              .catch((failure: unknown) => {
                setFeedbackProblem(
                  failure instanceof Error ? failure.message : 'That did not send.',
                );
              })
              .finally(() => setSending(false));
          }}
        >
          <label htmlFor="feedback" className="block font-bold">
            What would you like to tell us?
          </label>
          <textarea
            id="feedback"
            rows={4}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
          />
          <div className="flex items-center gap-3 min-h-control">
            <input
              id="feedback-anonymous"
              type="checkbox"
              checked={anonymous}
              onChange={(event) => setAnonymous(event.target.checked)}
              className="h-6 w-6"
            />
            <label htmlFor="feedback-anonymous">Send it without my name</label>
          </div>
          {feedbackProblem !== '' && (
            <p role="alert" className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0">
              {feedbackProblem}
            </p>
          )}
          <button
            type="submit"
            disabled={sending}
            className="control border-paper bg-paper/10 text-paper"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
      </section>
    </div>
  );
}

/**
 * "Booked for tomorrow" (ruling 78): how many orders are booked for tomorrow, by time of day, so
 * a Runner can plan a shift. Never who, where or what: those come only with an offer, on the day.
 */
function BookedForTomorrow(): JSX.Element | null {
  const [booked, setBooked] = useState<BookedTomorrow | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchBookedTomorrow()
      .then((found) => {
        if (!cancelled) setBooked(found);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  if (!booked || booked.total === 0) return null;
  const parts = (['morning', 'afternoon', 'evening'] as const)
    .filter((slot) => booked[slot] > 0)
    .map((slot) => `${booked[slot]} in the ${slot}`);
  return (
    <p className="m-0">
      <strong>
        Booked for tomorrow: {booked.total} {booked.total === 1 ? 'order' : 'orders'}
      </strong>
      {parts.length > 0 ? ` (${parts.join(', ')})` : ''}. They are offered on the day, like any
      order.
    </p>
  );
}
